// src/extensions/ResizableTable.js

import { Table } from 'tiptap-extensions'
import { columnResizing } from 'prosemirror-tables'
import { Plugin } from 'prosemirror-state'
import { Fragment } from 'prosemirror-model'

export default class ResizableTable extends Table {
  commands({ schema }) {
    return {
      // All other commands (including setCellAttr) come from the base class (Table)
      ...super.commands({ schema }),
      createTable:
        ({ rowsCount, colsCount, cellContent = null }) =>
        (state, dispatch) => {
          const { tr } = state
          const { table, table_row, table_cell, paragraph } = schema.nodes // Import paragraph node

          const cells = []
          for (let i = 0; i < colsCount; i += 1) {
            const defaultContent = cellContent
              ? Fragment.from(cellContent)
              : Fragment.from(paragraph.create()) // Create a default <p> if no cellContent

            const cell = table_cell.create(
              {
                colwidth: [100], // Set default column width
                style: 'width: 100px; padding: 10px; min-height: 50px;', // Add padding and min-height
              },
              defaultContent // Use default content
            )
            cells.push(cell)
          }

          const rows = []
          for (let i = 0; i < rowsCount; i += 1) {
            const row = table_row.create(null, Fragment.from(cells))
            rows.push(row)
          }

          const tableNode = table.create(null, Fragment.from(rows))
          tr.replaceSelectionWith(tableNode)

          dispatch(tr)
          return true
        },
    }
  }
  get plugins() {
    return [
      ...super.plugins,
      columnResizing(), // Enables column resizing
      // Plugin to apply styles dynamically to <td> or <th>
      new Plugin({
        props: {
          // Set inline styles for the <td> or <th> elements
          handleDOMEvents: {
            mouseup: (view, event) => {
              const { state, dispatch } = view
              const { selection } = state
              const tableNode = state.doc.nodeAt(selection.$anchor.pos)

              if (tableNode) {
                // Loop over the rows and cells
                tableNode.forEach((row, rowIndex) => {
                  row.forEach((cell, cellIndex) => {
                    const width = cell.attrs.colwidth
                      ? `${cell.attrs.colwidth[cellIndex]}px`
                      : 'auto'
                    dispatch(
                      state.tr.setNodeMarkup(selection.anchor - 1, null, {
                        ...cell.attrs,
                        style: `width: ${width}`,
                      })
                    )
                  })
                })
              }

              return false
            },
          },
        },
      }),
    ]
  }

  // Extend table schema to include colwidth
  get schema() {
    return {
      ...super.schema,
      attrs: {
        ...super.schema.attrs,
        colwidth: { default: null },
      },
      parseDOM: [
        {
          tag: 'table',
          getAttrs: (dom) => {
            const width = dom.getAttribute('colwidth')
            return {
              colwidth: width ? width.split(',').map(Number) : null,
            }
          },
        },
      ],
      toDOM: (node) => [
        'table',
        { style: node.attrs.style || 'border-collapse: collapse;' },
        ['tbody', 0],
      ],
    }
  }
}
